/*
<summary>
   This functional component renders the Equipment Inspection Menu - a single header bar
   (Back, title, a Crimp Inspection / User Created tab switch styled like Tool Management's
   tool-type switch, and Refresh/Export CSV actions), a shared inline filter bar, and the
   selected tab's inspection record list module. The filter and Refresh/Export actions are
   common to both tabs - which store/API they hit depends on whichever tab is active. The
   active tab is persisted to localStorage so it's restored on the next visit.
   Developer: Aashish Singh, Created Date: 01-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useStore } from '../../contexts/store-provider';
import { ISharedInspectionFilter, SharedInspectionFilterInitialState } from '../../models/ISharedInspectionFilter';
import { getTenantAccessFromLocalStorage } from '../../helpers/localstorage-helper';
import RoutesConstants from '../../shared-components/routes/routes-constants';
import InspectionFilter from '../../shared-components/inspection-filter/inspection-filter';
import { RefreshIcon, ExportIcon, ExportHolidayIcon } from '../../shared-components/svgs/svgs';
import { ToolCrimpInspectionIcon, ToolUserInspectionIcon } from '../../shared-components/svgs/module-svgs';
import { formatMessage } from '../../translations/format-message';
import CrimpingInspectionRecord from '../crimping-inspection-record/crimping-inspection-record';
import UserCreatedInspectionRecord from '../user-created-inspection-record/user-created-inspection-record';
import './equipment-inspection-menu.css';

type InspectionTab = 'user_created' | 'crimp';

const ACTIVE_TAB_STORAGE_KEY = 'equipment_inspection_menu_active_tab';

/**
* This function reads the last-selected tab from localStorage, defaulting to User Created
* when nothing was saved yet or the value isn't recognized.
*/
const getStoredActiveTab = (): InspectionTab => {
  const stored = window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
  return stored === 'crimp' ? 'crimp' : 'user_created';
};

/**
* Renders the Equipment Inspection Menu - header bar with a Crimp Inspection / User Created
* tab switch and shared Refresh/Export actions, a shared filter bar, and the selected tab's
* inspection record list module.
*/
const EquipmentInspectionMenu = () => {
  const history = useHistory();
  const { factoryStore, crimpingInspectionRecordStore, userInspectedListStore } = useStore();
  const { allAvailableFactories, GetAllFactoriesService } = factoryStore;
  const { exportCrimpingInspectionRecordFileState,resetToolCrimpInspectionList } = crimpingInspectionRecordStore;
  const { exportUserInspectedListState, reset } = userInspectedListStore;

  const [activeTab, setActiveTab] = useState<InspectionTab>(getStoredActiveTab);
  const [filterObj, setFilterObj] = useState<ISharedInspectionFilter>(() => {
    const accessedFactoryId = getTenantAccessFromLocalStorage();
    return accessedFactoryId !== -1
      ? { ...SharedInspectionFilterInitialState, OwnerFactoryId: accessedFactoryId }
      : SharedInspectionFilterInitialState;
  });
  const [isFilterCleared, setIsFilterCleared] = useState(true);
  // Bumped whenever the child list (for the active tab) should re-fetch with filterObj -
  // on Search, Clear, and Refresh.
  const [filterVersion, setFilterVersion] = useState(0);

  useEffect(() => {
    GetAllFactoriesService(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  // Clear only the inspection lists when leaving this menu. Other store state, such as
  // export progress and inspection-session data, remains untouched.
  useEffect(() => () => {
    resetToolCrimpInspectionList();
    reset();
  }, [crimpingInspectionRecordStore, userInspectedListStore]);


  const handleRefresh = () => {
    setFilterVersion((prev) => prev + 1);
  };

  const handleExportClick = () => {
    if (activeTab === 'crimp') {
      crimpingInspectionRecordStore.ExportCrimpingInspectionRecordFileService(filterObj);
    } else {
      userInspectedListStore.ExportUserInspectedListService(filterObj);
    }
  };

  /**
  * This function is used to apply the filter values and signal the active tab's list to
  * re-fetch with them.
  * @param object: Filter values submitted from the filter form
  */
  const filterHandler = (object: ISharedInspectionFilter) => {
    setFilterObj({ ...object });
    setIsFilterCleared(false);
    setFilterVersion((prev) => prev + 1);
  };

  /**
  * This function is used to update the filter values, and signal a re-fetch when the filter
  * is cleared.
  * @param obj: Filter values
  * @param isfilterCleard: Flag indicating the filter was cleared
  */
  const filterParams = (obj: ISharedInspectionFilter, isfilterCleard: boolean) => {
    setFilterObj({ ...obj });
    if (isfilterCleard) {
      setIsFilterCleared(true);
      setFilterVersion((prev) => prev + 1);
    }
  };

  const isExportInProgress =
    exportCrimpingInspectionRecordFileState.inProgress || exportUserInspectedListState.inProgress;

  return (
    <div className="eim-page">
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-xl-4 col-lg-5 col-md-6 col-sm-6">
            <div className="page_title_wrap">
              <ul className="add-list operator-back">
                <li className="process-chart">
                  <a
                    className="btn btn-secondary"
                    onClick={() => history.push(RoutesConstants.EquipmentManagement)}
                  >
                    <FormattedMessage id="back" />
                  </a>
                </li>
                <li>
                  <h1 className="page_title"><FormattedMessage id="equipment_inspection_menu" /></h1>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-xl-8 col-lg-7 col-md-6 col-sm-6">
            <ul className="add-list">
              <ul className="tableAccordian-switch tm-tool-type-switch d-flex bg-white w-auto rounded-1">
                <li
                  className={`d-flex align-items-center rounded-1 ${
                    activeTab === 'user_created' ? 'active text-white' : ''
                  }`}
                  onClick={() => setActiveTab('user_created')}
                >
                  <ToolUserInspectionIcon />
                  <span className="icon-text"><FormattedMessage id="user_created" /></span>
                </li>
                <li
                  className={`d-flex align-items-center rounded-1 ${
                    activeTab === 'crimp' ? 'active text-white' : ''
                  }`}
                  onClick={() => setActiveTab('crimp')}
                >
                  <ToolCrimpInspectionIcon />
                  <span className="icon-text"><FormattedMessage id="crimp_inspection" /></span>
                </li>
              </ul>
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={handleRefresh}
                >
                  <RefreshIcon />
                </a>
              </li>
            
              <li title={formatMessage('export_csv')}>
                <a
                  className={`btn btn-light ${isExportInProgress ? 'disabled' : ''}`}
                  onClick={isExportInProgress ? undefined : handleExportClick}
                >
                  <ExportHolidayIcon />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filter and grid share one card so the page reads as one continuous block, not two
          separately-boxed modules. */}
      <div className="card card-page eim-content-card uci-card">
        <InspectionFilter
          initialValues={filterObj}
          submitHandler={filterHandler}
          filterParmas={filterParams}
          isLoading={false}
          isFilterCleared={isFilterCleared}
          factoryOptions={allAvailableFactories}
        />

        {activeTab === 'user_created' ? (
          <UserCreatedInspectionRecord
            embedded
            sharedFilter={filterObj}
            filterVersion={filterVersion}
          />
        ) : (
          <CrimpingInspectionRecord
            embedded
            sharedFilter={filterObj}
            filterVersion={filterVersion}
          />
        )}
      </div>
    </div>
  );
};

export default observer(EquipmentInspectionMenu);
